import { buscarEscritorioPorId } from '@/features/escritorio';

import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  BuilderDaSimulacao,
  DesfechoDoInicio,
  ErroDaSimulacao,
  NegociacaoSimulacaoState,
} from '../../types';
import { desfechoDesconhecido } from '../../utils/intencaoDeComando';
import {
  intencaoRegistrada,
  agenteSelecionado,
  dadoAlterado,
  estadoDaSimulacaoRecebido,
} from '../eventosDoStore';
import {
  reiniciarContexto,
  descartarConversa,
  descartarInicio,
  assumirSimulacao,
  recuperarFormulario,
  atualizarSeForAtual,
} from '../transicoesDaSimulacao';
import { iniciarSimulacaoDoAdmin } from '../admin/actions';
import {
  buscarAcessoSimulacao,
  iniciarSimulacao,
  buscarSimulacaoAtual,
  encerrarSimulacao,
} from './actions';

const inicioPendente = (state: NegociacaoSimulacaoState) => {
  state.iniciando = true;
  state.erroDoInicio = null;
};

const inicioConcluido = (
  state: NegociacaoSimulacaoState,
  action: PayloadAction<DesfechoDoInicio>,
) => {
  state.iniciando = false;
  delete state.intencoes.iniciar;
  const { estado, descartada } = action.payload;
  if (descartada) {
    descartarInicio(state, estado.id);
    return;
  }
  state.marcoDaSimulacao += 1;
  assumirSimulacao(state, estado);
  state.erroDoEncerramento = null;
};

// O formulário não é tocado: os valores ficam para a nova tentativa (CA-4).
const inicioRecusado = (
  state: NegociacaoSimulacaoState,
  action: { payload: ErroDaSimulacao | undefined },
) => {
  state.iniciando = false;
  state.erroDoInicio = action.payload ?? null;
  if (!desfechoDesconhecido(state.erroDoInicio)) delete state.intencoes.iniciar;
};

export function registrarCicloDaSimulacao(builder: BuilderDaSimulacao): void {
  builder
    // O escritório do admin é o escolhido na tela, e não o workspace de quem está logado.
    .addCase(buscarEscritorioPorId.pending, (state, action) => {
      if (state.superficie === 'ADMIN') return;
      if (state.escritorioId === action.meta.arg) {
        state.requisicaoDoEscritorio = action.meta.requestId;
        return;
      }
      return {
        ...reiniciarContexto(state, action.meta.arg),
        requisicaoDoEscritorio: action.meta.requestId,
        trocandoEscritorio: true,
      };
    })
    .addCase(buscarEscritorioPorId.fulfilled, (state, action) => {
      if (state.superficie === 'ADMIN') return;
      if (state.requisicaoDoEscritorio !== action.meta.requestId) return;
      state.requisicaoDoEscritorio = null;
      state.trocandoEscritorio = false;
    })
    .addCase(buscarEscritorioPorId.rejected, (state, action) => {
      if (state.superficie === 'ADMIN') return;
      if (state.requisicaoDoEscritorio !== action.meta.requestId) return;
      if (!state.trocandoEscritorio) {
        state.requisicaoDoEscritorio = null;
        return;
      }
      return reiniciarContexto(state, null);
    })
    .addCase(intencaoRegistrada, (state, action) => {
      state.intencoes[action.payload.tipo] = action.payload.intencao;
    })
    .addCase(buscarAcessoSimulacao.pending, (state, action) => {
      // O menu do cliente relê o acesso pelo workspace; no admin, o escritório é o da tela.
      if (state.superficie !== 'ADMIN') state.escritorioId ??= action.meta.arg;
      state.acesso.carregando = true;
      state.acesso.requisicaoVigente = action.meta.requestId;
    })
    // Resposta atrasada do workspace anterior não sobrescreve a do atual.
    .addCase(buscarAcessoSimulacao.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.acesso.requisicaoVigente) return;
      state.acesso.podeUsar = action.payload.podeUsar;
      state.acesso.carregadoPara = action.payload.escritorioId;
      state.acesso.carregando = false;
      state.acesso.erro = null;
    })
    .addCase(buscarAcessoSimulacao.rejected, (state, action) => {
      if (action.meta.requestId !== state.acesso.requisicaoVigente) return;
      state.acesso.podeUsar = false;
      state.acesso.carregadoPara = action.meta.arg;
      state.acesso.carregando = false;
      state.acesso.erro = action.payload ?? null;
    })
    // Valores do agente anterior nunca vão para o novo (CA-5).
    .addCase(agenteSelecionado, (state, action) => {
      state.formulario = { flowId: action.payload, valores: {} };
      state.agente = { dados: null, carregando: false, erro: null, requisicaoId: null };
      state.versaoDoFormulario += 1;
      state.ultimaAlteracaoDoFormulario = 'AGENTE_TROCADO';
      state.erroDoInicio = null;
      descartarConversa(state);
    })
    .addCase(dadoAlterado, (state, action) => {
      state.formulario.valores[action.payload.nome] = action.payload.valor;
      state.versaoDoFormulario += 1;
      state.ultimaAlteracaoDoFormulario = 'DADOS_ALTERADOS';
      if (state.erroDoInicio) {
        state.erroDoInicio.camposInvalidos = state.erroDoInicio.camposInvalidos.filter(
          (campo) => campo !== action.payload.nome,
        );
      }
      descartarConversa(state);
    })
    .addCase(iniciarSimulacao.pending, inicioPendente)
    .addCase(iniciarSimulacao.fulfilled, inicioConcluido)
    .addCase(iniciarSimulacao.rejected, inicioRecusado)
    .addCase(iniciarSimulacaoDoAdmin.pending, inicioPendente)
    .addCase(iniciarSimulacaoDoAdmin.fulfilled, inicioConcluido)
    .addCase(iniciarSimulacaoDoAdmin.rejected, inicioRecusado)
    // Leitura que saiu antes de a pessoa trocar o agente não traz de volta a simulação deixada.
    .addCase(buscarSimulacaoAtual.fulfilled, (state, action) => {
      state.falhaDaSincronizacao.atual = false;
      const { estado, marco } = action.payload;
      if (marco !== state.marcoDaSimulacao) return;
      if (estado && estado.id === state.simulacaoDescartadaId) return;
      if (estado) recuperarFormulario(state, estado);
      assumirSimulacao(state, estado);
    })
    .addCase(buscarSimulacaoAtual.rejected, (state) => {
      state.falhaDaSincronizacao.atual = true;
    })
    .addCase(estadoDaSimulacaoRecebido, (state, action) => {
      atualizarSeForAtual(state, action.payload);
    })
    .addCase(encerrarSimulacao.pending, (state) => {
      state.erroDoEncerramento = null;
    })
    .addCase(encerrarSimulacao.fulfilled, (state, action) => {
      atualizarSeForAtual(state, action.payload);
    })
    .addCase(encerrarSimulacao.rejected, (state, action) => {
      if (state.simulacao && state.simulacao.id !== action.meta.arg.simulacaoId) return;
      state.erroDoEncerramento = action.payload ?? null;
    });
}
